import React, { useState } from 'react';
import { X, FileDown } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { IndentPDF } from './IndentPDF';

export const ExportPopup = ({ onClose, exportEndpoint, indentData, customFilename = 'export' }) => {
  console.log('ExportPopup indentData:', indentData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const processIndentData = (item) => {
    console.log('processIndentData input:', item.L2ApprovalDate, item.L2ApprovedDate);
    
    const groupedItems = item.items?.map((item) => ({
      itemName: item.ItemName || 'N/A',
      quantity: item.Quantity || 'N/A',
      unit: item.UnitName || 'N/A',
      category: item.CategoryName || 'N/A',
      remarks: item.Remarks || 'N/A'
    })) || [];

    // Format dates consistently
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      try {
        // If it's already in dd/mm/yy format, return as is
        if (typeof dateStr === 'string' && dateStr.includes('/')) {
          return dateStr;
        }
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Date formatting error:', error);
        return 'N/A';
      }
    };

    // Convert approval status to consistent format
    const getApprovalStatus = (value) => {
      if (value === 1 || value === "1" || value === true || value === "Approved") {
        return 1;
      }
      return 0;
    };

    // Defensive fallback for L2ApprovalDate
    let l2Raw = item?.L2ApprovalDate;
    if (!l2Raw || l2Raw === 'N/A') l2Raw = item?.L2ApprovedDate;

    const processed = {
      indentId: item?.IndentID || 'N/A',
      creationDate: formatDate(item?.CreationDate),
      PEApprovalDate: formatDate(item?.PEApprovalDate || item?.PEApprovedDate),
      L1ApprovedDate: formatDate(item?.L1ApprovedDate || item?.L1ApprovalDate),
      L2ApprovalDate: formatDate(l2Raw),
      projectName: item?.ProjectName || 'N/A',
      taskScheduled: item?.StageName || item?.TaskSchedule || 'N/A',
      status: item?.Status || 'N/A',
      invoicingType: item?.TypeName || 'N/A',
      userName: item?.UserName || 'N/A',
      L1A: getApprovalStatus(item?.L1A),
      L1: getApprovalStatus(item?.L1),
      L2: getApprovalStatus(item?.L2),
      PO: getApprovalStatus(item?.PO),
      PH: getApprovalStatus(item?.PH),
      IR: getApprovalStatus(item?.IR),
      SH: getApprovalStatus(item?.SH),
      items: groupedItems
    };
    
    console.log('processIndentData output:', processed.L2ApprovalDate);
    return processed;
  };

  const handleExport = async (format) => {
    setIsLoading(true);
    setError('');
    
    try {
      if (format === 'pdf') {
        // const processedData = processIndentData(indentData);
        const processedData = indentData; // TEMP: Pass raw data directly for debugging
        console.log("Processed data for PDF:", processedData);
        
        if (!processedData || (Array.isArray(processedData) && processedData.length === 0)) {
          throw new Error('No data available for export');
        }
        
        const blob = await pdf(<IndentPDF data={processedData} />).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${customFilename}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        onClose();
        return;
      }

      const response = await fetch(`${exportEndpoint}?format=${format}`);
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${customFilename}.${format}`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onClose();
    } catch (err) {
      setError('Failed to export data. Please try again.');
      console.error('Export error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Export Data</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
          <button
            onClick={() => handleExport('csv')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as CSV
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as PDF
          </button>
        </div>
      </div>
    </div>
  );
};